import { supabase } from "@/integrations/supabase/client";
import type { EventType } from "@/types";
import { noteUserInteraction } from "@/lib/halftimeAutoOpenPolicy";
import { getBatteryMode } from "@/lib/batteryMode";
import { shouldKeep } from "@/lib/pr40a/auditSampling";

// PR-16: batch in battery_saver to minimize radio wakeups.
// PR-40A: batch ALWAYS (5s window / 25 events) to cut Postgres write load
// during World Cup goal-moment storms. Critical events still flush
// immediately on visibility change / pagehide so we don't lose receipts.
type Pending = { event_type: EventType; tenant_id: string; event_data: Record<string, unknown>; user_id: string | null };
const pending: Pending[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const NORMAL_FLUSH_MS = 5_000;
const NORMAL_MAX_BATCH = 25;
const SAVER_FLUSH_MS = 60_000;

// Cached current user id so trackEvent stays non-blocking. Refreshed on
// auth state changes. Enables Creator Studio to attribute audit events
// back to the creator (shares/clicks). No engine behavior changes.
let cachedUserId: string | null = null;
if (typeof window !== "undefined") {
  void supabase.auth.getSession().then(({ data }) => {
    cachedUserId = data.session?.user?.id ?? null;
  }).catch(() => {});
  supabase.auth.onAuthStateChange((_e, session) => {
    cachedUserId = session?.user?.id ?? null;
  });
}

async function flushPending() {
  flushTimer = null;
  if (pending.length === 0) return;
  const batch = pending.splice(0, pending.length);
  try {
    await supabase.from("audit_master").insert(batch as never);
  } catch (e) {
    console.warn("[audit] batch flush failed", e);
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.hidden) void flushPending();
  });
  window.addEventListener("pagehide", () => { void flushPending(); });
}

export async function trackEvent(
  event_type: EventType,
  event_data: Record<string, unknown> = {},
  tenant_id = "univision-demo",
) {
  // PR-14A: arm the Halftime Hub auto-open lockout on any user interaction.
  try { noteUserInteraction(event_type); } catch { /* noop */ }
  // Activation sequence — fan-out a lightweight window event so the
  // POST-NATIONGATE ACTIVATION listener can advance steps without
  // touching Prediction Engine, Share Engine or Nation Wars scoring.
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("vs:event", { detail: { event_type, event_data } }));
    } catch { /* noop */ }
  }
  const user_id = cachedUserId;
  // PR-40A: priority sampling. Critical events kept 1:1, retention 1:5,
  // diagnostic 1:20. Sample rate is stamped into event_data so analytics
  // can scale counts back up.
  const { keep, rate } = shouldKeep(event_type);
  if (!keep) return { ok: true, sampled_out: true, rate };
  const stamped =
    rate === 1 ? event_data : { ...event_data, _sample_rate: rate };
  // PR-40A: always-on batching (saver flushes slower).
  const flushMs = getBatteryMode() === "battery_saver" ? SAVER_FLUSH_MS : NORMAL_FLUSH_MS;
  pending.push({ event_type, tenant_id, event_data: stamped, user_id });
  if (pending.length >= NORMAL_MAX_BATCH) { void flushPending(); return { ok: true, batched: true, rate }; }
  if (!flushTimer) flushTimer = setTimeout(() => { void flushPending(); }, flushMs);
  return { ok: true, batched: true, rate };
}